import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { NavigationButton } from '@/components/NewSidebar/components/NavigationButton';
import { useNavigateToAdvancedEditor } from '@/hooks/navigation';
import { useNavigateToAgentsEditor } from '@/hooks/navigation/useNavigateToAgentsEditor';
import Dialog from '@/images/Dialog.svg';
import VideoLibrary from '@/images/VideoLibrary.svg';
import { isExpressPresenter, isStreamablePresenter, Presenter, VideoType } from '@d-id/types';
import styled from '@emotion/styled';
import { Popover, Tooltip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';

const PopoverContent = styled.div`
    padding: 8px;
    border-radius: 8px;

    button {
        width: 100%;
    }
`;

const StyledNavigationButton = styled(NavigationButton, {
    shouldForwardProp: prop => prop !== 'hide',
})<{ hide?: boolean }>`
    ${({ hide }) => hide && 'display: none;'}
`;
const StyledTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)`
    ${`& .${tooltipClasses.tooltip}`} {
        background-color: black;
        padding: 8px;

        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        margin-bottom: -10px;
        margin-left: 8px;
    }
`;

interface Props {
    avatar: Presenter;
    anchor: HTMLButtonElement | null;
    setAnchor: (value: HTMLButtonElement | null) => void;
}

export const AvatarUsePopover = ({ avatar, anchor, setAnchor }: Props) => {
    const { navigateToAdvancedEditor, isLoading } = useNavigateToAdvancedEditor();
    const { navigateToAgentEditor } = useNavigateToAgentsEditor();

    const handleUseAvatarClose = () => {
        setAnchor(null);
    };

    const avatarPopoverOpen = Boolean(anchor);
    const id = avatarPopoverOpen ? 'avatar-popover' : undefined;

    const handleUseAvatarInVideo = async () => {
        await navigateToAdvancedEditor({ options: { initialAvatar: avatar, from: 'Avatars' }, mixPanelFrom: 'avatar-use-popover' });
    };

    const handleUseAvatarInAgents = () => {
        navigateToAgentEditor({ initialAvatar: avatar, mixPanelFrom: 'avatar-use-popover', from: '/avatars' });
    };

    const canUseAvatarInAgents = isStreamablePresenter(avatar) && avatar.type !== VideoType.EXPRESSIVE;
    const hideAgents = isExpressPresenter(avatar);

    return (
        <Popover
            id={id}
            open={avatarPopoverOpen}
            anchorEl={anchor}
            onClose={handleUseAvatarClose}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            sx={{ marginTop: -1 }}>
            <PopoverContent>
                <StyledNavigationButton onClick={handleUseAvatarInVideo} disabled={isLoading}>
                    {isLoading ? <CircularProgress size={20} /> : <MaskedIcon src={VideoLibrary} size="24px" />}
                    Avatar Video
                </StyledNavigationButton>

                <StyledTooltip title="Coming soon" placement="top-start" disableHoverListener={canUseAvatarInAgents}>
                    <div>
                        <StyledNavigationButton onClick={handleUseAvatarInAgents} disabled={!canUseAvatarInAgents} hide={hideAgents}>
                            <MaskedIcon src={Dialog} size="24px" />
                            Agent
                        </StyledNavigationButton>
                    </div>
                </StyledTooltip>
            </PopoverContent>
        </Popover>
    );
};
